import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const dashPath = (role) => ({ super_admin: "/admin", admin: "/admin", sub_admin: "/admin", educator: "/educator", content_seller: "/seller", institute: "/institute", learner: "/learner" }[role] || "/learner");

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate(location.state?.from?.pathname || dashPath(data.user.role));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthShell title="Welcome back" subtitle="Login to continue your learning journey">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><Label>Email or Mobile</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email" required /></div>
        <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" required /></div>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember me</label>
          <Link to="/forgot-password" className="text-amber font-semibold">Forgot password?</Link>
        </div>
        {error && <p className="text-crimson text-sm" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full viriks-navy text-white" disabled={loading} data-testid="login-submit">{loading ? "Logging in..." : "Login"}</Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" /></div>
      <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login">Continue with Google</Button>
      <p className="text-center text-sm text-muted-foreground mt-6">Don't have an account? <Link to="/register" className="text-amber font-semibold">Sign up</Link></p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex viriks-navy text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 hero-noise opacity-30" />
        <Link to="/" className="relative z-10 inline-block">
          <div className="inline-flex bg-white rounded-lg p-2"><img src="/viriks-logo.jpg" alt="VIRIKS" className="h-9 w-auto" /></div>
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-bold leading-tight">Learn. Grow. Succeed.</h2>
          <p className="text-white/60 mt-4 text-lg">Courses, live classes, test series & more — everything to help you succeed.</p>
        </div>
        <p className="text-white/40 text-sm relative z-10">© {new Date().getFullYear()} VIRIKS EdTech</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><img src="/viriks-logo.jpg" alt="VIRIKS" className="h-9 w-auto" /></Link></div>
          <h1 className="font-heading text-3xl font-bold text-navy">{title}</h1>
          <p className="text-muted-foreground mt-1 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
